<script setup lang="ts">
import { computed, ref } from 'vue';
import FileUpload from 'primevue/fileupload';
import { storeToRefs } from 'pinia';
import PreviewMessageFile from '@/components/MessageCenter/components/Chat/components/PreviewMessageFile.vue';
import PreviewFileModal from '@/components/MessageCenter/components/Chat/components/PreviewFileModal.vue';
import Dialog from 'primevue/dialog';
import useFileList from '@/composables/useFileList';
import { EMIT } from '@/enum';
import { FileInterface, FileTypes } from '@/types';
import { useMessageCenterStore } from '@/store';

const emit = defineEmits<{
  (eventName: EMIT.SEND_MESSAGE_FILE, messageData: FormData): void;
  (eventName: EMIT.CANCEL): void;
}>();

const messageCenterStore = useMessageCenterStore();
const { getFileConfigurations, getFileTypes, messageText } = storeToRefs(messageCenterStore);
const { files, addFiles, removeFile } = useFileList();
const modalVisible = ref<boolean>(true);
const previewBlob = ref<Blob | File | undefined>(undefined);
const previewVisible = ref<boolean>(false);

const allowedFiles = computed(() => {
  const types = (getFileTypes.value ?? []) as FileTypes[];
  const exts = types
    .map((t) => (typeof t?.ext === 'string' ? t.ext.trim() : ''))
    .map((ext) => (ext.startsWith('.') ? ext : `.${ext}`));
  return Array.from(new Set(exts)).join(', ');
});

const maxFileSizeMB = computed<number>(() => {
  return getFileConfigurations.value?.max_upload_size || 1024;
});

const maxFileSizeBytes = computed<number>(() => maxFileSizeMB.value * 1024 * 1024);

const maxFilesAllowed = computed<number>(() => {
  return getFileConfigurations.value?.max_files || 1;
});

const sendMessageFile = async (): Promise<void> => {
  if (!files.value.length) {
    messageCenterStore.doToast('Please select at least one file to send.', 'Error', true);
    return;
  }

  const formData = new FormData();
  formData.append('content', (messageText.value ?? '').toString());
  formData.append('type', 'file');

  const localFiles = [...(files.value as FileInterface[])];
  for (const file of localFiles) {
    if (file?.file instanceof Blob && typeof file.name === 'string' && file.name) {
      formData.append('attachment[]', file.file as Blob, file.name);
    }
  }

  if (formData.getAll('attachment[]').length === 0) {
    messageCenterStore.doToast('Please select at least one valid file to send.', 'Error', true);
    removeFiles(localFiles);
    modalVisible.value = false;
    emit(EMIT.CANCEL);
    return;
  }

  emit(EMIT.SEND_MESSAGE_FILE, formData);

  removeFiles(localFiles);
  messageText.value = '';
  previewVisible.value = false;
  previewBlob.value = undefined;
  modalVisible.value = false;
};

const onDrop = (e: { files: File[] }) => {
  if (e.files.length > maxFilesAllowed.value) {
    messageCenterStore.doToast(`You can only upload a maximum of ${maxFilesAllowed.value} files.`, 'Error', true);
    return;
  }

  addFiles(e.files);
};

const formDisabled = computed(() => {
  const text = (messageText.value ?? '').trim();
  return text.length === 0 && files.value.length === 0;
});

const cancel = () => {
  messageCenterStore.messageText = '';
  if (files.value.length > 0) removeFiles([...files.value]);

  emit(EMIT.CANCEL);
  modalVisible.value = false;
};

const removeFiles = (files: FileInterface[]) => {
  files.forEach((file) => removeFile(file));
};

const onShowPreview = (file: Blob | File) => {
  previewBlob.value = file;
  previewVisible.value = true;
};

const onCancelPreview = () => {
  previewVisible.value = false;
  previewBlob.value = undefined;
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    class="dialog__container add-document__modal"
    :modal="true"
    :draggable="false"
    @hide="cancel"
  >
    <template #header>
      <div class="dialog__header flex align-items-center">
        <h2 class="m-0" v-text="'Add File'" />
        <Icon v-tooltip="`Supported file types: ${allowedFiles}`" name="info" class="ml-2 text-gray-500 text-s" />
      </div>
    </template>

    <div
      class="upload-wrapper flex relative text-center align-items-center justify-content-center w-full"
      :style="{ height: '98px' }"
    >
      <FileUpload
        :custom-upload="true"
        class="upload relative"
        choose-label="Choose file"
        :invalid-file-type-message="'Sorry, this file type is not supported. Please select a supported file type: {1}'"
        :auto="true"
        mode="advanced"
        :accept="allowedFiles || ''"
        :show-cancel-button="false"
        :show-upload-button="false"
        :max-file-size="maxFileSizeBytes"
        :multiple="true"
        invalid-file-size-message="Error: This file exceeds the max file size allowed."
        invalid-file-limit-message="Error: You can only upload a maximum of {0} files."
        @uploader="onDrop"
      >
        <template #empty>
          <p class="uploader-text font-bold text-base mt-2">
            <span class="underline cursor-pointer">Choose File</span>
            or drag and drop here
          </p>
          <p class="acceptable-files-text">Max File size: {{ maxFileSizeMB }}MB</p>
        </template>
      </FileUpload>
    </div>
    <div
      v-if="files.length > 0"
      class="upload-wrapper flex relative text-center align-items-center justify-content-center w-full"
      :style="!files.length ? { height: '98px' } : {}"
      @dragover.prevent
      @drop.prevent="
        (event) => {
          event.preventDefault();
        }
      "
    >
      <ul class="upload-wrapper__file-list flex w-full left-0 align-items-center">
        <PreviewMessageFile
          v-for="file of files"
          :key="file.id"
          class="w-full flex text-left align-items-center"
          :file="file"
          tag="li"
          @show-preview="onShowPreview"
          @remove="removeFile"
        />
      </ul>
    </div>
    <div class="field w-full message-field mt-4">
      <label for="message">Message</label>
      <InputText
        id="message"
        v-model="messageText"
        class="message-field__input w-full"
        name="message"
        @keyup.enter="sendMessageFile"
      />
    </div>
    <template #footer>
      <AppButton type="secondary" text="Cancel" @click="cancel" />
      <AppButton type="primary" text="Send" :disabled="formDisabled" @click="sendMessageFile" />
    </template>
  </Dialog>
  <PreviewFileModal :visible="previewVisible" :title="'Preview File'" :file="previewBlob" @cancel="onCancelPreview" />
</template>

<style lang="scss" scoped>
.dialog__container {
  .p-dialog-content {
    padding-top: 0;

    .upload-wrapper {
      padding-top: 0;
      margin-top: 0;
      align-items: flex-start;

      :deep(.p-fileupload-buttonbar) {
        padding-top: 0;
        background: none;
        border: none;

        .p-button {
          margin-left: -87%;
          padding: 6px;
          font-weight: bold;
          cursor: pointer;
          z-index: 5;
          border: none;

          .p-button-label {
            text-decoration: underline;
            font-size: 16px;
          }
          .p-button-icon {
            display: none;
          }
        }
      }
      :deep(.p-fileupload-content) {
        text-align: center;
        border: 2px dashed $grayMedium;
        border-radius: 8px;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        background: transparent;
        display: flex;
        justify-content: center;

        .p-message-error {
          position: absolute;
          top: -51px;
          width: 100%;
        }
        .p-fileupload-empty {
          position: relative;
          top: -10px;
          .uploader-text {
            color: var(--primary-color-dark);
            line-height: 18px;
            margin-bottom: 4px;
          }

          .acceptable-files-list {
            color: var(--primary-color-dark);

            ul {
              list-style-type: none;
              padding-left: 0;

              li {
                margin-bottom: 4px;
              }

              code {
                background-color: #f1f1f1;
                padding: 2px 5px;
                border-radius: 4px;
                font-size: 11px;
              }
            }
          }

          .acceptable-files-text {
            font-size: 12px;
            line-height: 15px;
            color: var(--primary-color-dark);
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 6px;
            text-align: center;
          }
        }
      }
      :deep(.p-fileupload-highlight) {
        border-color: var(--primary-color);
      }

      .upload-wrapper__file-list {
        margin-top: 0.25rem;
        flex-direction: column;
        color: $grayDark;
        font-size: 14px;
        line-height: 18px;
        border: 2px solid $grayMedium;
        border-radius: 8px;
        max-height: 220px;
        overflow-y: auto;
        padding: 1rem 2rem;
        top: -14px;
      }

      .v-enter-active,
      .v-leave-active {
        transition: opacity 0.5s ease;
      }
      .v-enter-from,
      .v-leave-to {
        opacity: 0;
      }
      .upload-wrapper__error {
        color: #822508;
        top: -65px;
        height: 37px;
        z-index: 6;
      }
    }

    .message-field {
      margin-top: 6px;
      margin-bottom: 6px;

      .message-field__input {
        margin: 2px 0;
      }
    }
  }
}
</style>
